import { useContext, useEffect } from 'react';
import { IWallV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { SelectedMarkerV2Common } from 'components/site/general/singleSelect/SelectedMarkerV2/SelectedMarkerV2Common';
import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { rotateAndScalePolygon } from 'hooks/coordinates/useCoordinatesComputations';
import { ICoordinates } from 'libs/types/map/mapTypes';
import polylabel from 'polylabel';

interface Props {
    markerId: string;
}

export const SelectedMarkerV2Wall = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    const marker = ctxEdit.worldMarkers.find((e) => e.id === props.markerId) as IWallV2WorldMarker;
    const isLine = marker.elementData.line.isLine;

    const updateLineDirectly = (newCoordinates: ICoordinates[]) => {
        const newHeight = (marker.elementData.wall.baseHeight ?? 0) + 0.01;
        directMarkerUpdates.updateWallHeight(marker.id, newHeight);

        directMarkerUpdates.updateLineWall(
            marker.id,
            newCoordinates,
            marker.elementData.line.width,
            marker.elementData.line.isLine && marker.elementData.line.hasFloor
        );
    };

    const updatePolygonDirectly = (newCoordinates: ICoordinates[]) => {
        const newHeight = (marker.elementData.wall.baseHeight ?? 0) + 0.01;
        directMarkerUpdates.updateWallHeight(marker.id, newHeight);

        directMarkerUpdates.updatePolygon(marker.id, newCoordinates, 'wall', {
            closePolygon: false
        });
    };

    const updateMarkerGlobally = (newCoordinates: ICoordinates[]) => {
        const [lat, lng] = polylabel([newCoordinates.map(({ lat, lng }) => [lat, lng])]);

        const nonTransformedCoordinates = rotateAndScalePolygon(
            newCoordinates,
            -marker.elementData.rotation,
            1.0 / marker.elementData.scale
        );

        ctxEdit.updateWorldMarker(
            props.markerId,
            { lat, lng },
            { coordinates: newCoordinates, nonTransformedCoordinates }
        );
    };

    useEffect(() => {
        return () => {
            const newHeight =
                (marker.elementData.wall.baseHeight ?? 0) + marker.elementData.wall.height;
            directMarkerUpdates.updateWallHeight(marker.id, newHeight);
        };
    }, [directMarkerUpdates]);

    return (
        <SelectedMarkerV2Common
            markerId={marker.id}
            coordinates={marker.elementData.coordinates}
            isClosedPolygon={!isLine}
            minCoordinatesCount={2}
            updateMarkerDirectly={isLine ? updateLineDirectly : updatePolygonDirectly}
            updateMarkerGlobally={updateMarkerGlobally}
            color={marker.elementData.wall.color}
        />
    );
};
